import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { useSystemVarsStore } from '../../state/useSystemVarsStore';

/**
 * PDMODE point marker (Faz 7.3 — OCS entities/point.rs bit model).
 *
 * - Low nibble: 0 = dot, 1 = nothing (still selectable), 2 = '+', 3 = '×',
 *   4 = '|' (tick).
 * - +32 draws a circle, +64 a square around the point (96 = both).
 * - Arms extend beyond the box/circle: arm length = 2 * half (OCS 2*s_half).
 * - PDSIZE > 0 absolute world size; = 0 → 5% of the viewport height;
 *   < 0 → |value| percent of the viewport height. Zoom-stable: the unit
 *   glyph rescales per frame from the live camera.
 * - The DEFPOINTS layer (case-insensitive, xref-prefixed included) forces
 *   the 0-dot style so construction points never plot as glyphs.
 * - data.xAxisAngle (DXF 50) rotates the glyph.
 */

/** True for the DEFPOINTS layer family (xref|DEFPOINTS included). */
export function isDefPointsLayer(layerName: string | undefined): boolean {
    if (!layerName) return false;
    const short = layerName.split('|').pop() ?? layerName;
    return short.trim().toLowerCase() === 'defpoints';
}

/** Unit-size geometry (marker size 1 → half = 0.5, arms = 1.0). */
function glyphLines(shape: number, hasCircle: boolean, hasSquare: boolean): [number, number, number][][] {
    const lines: [number, number, number][][] = [];
    const ARM = 1.0;
    const HALF = 0.5;
    const add = (a: [number, number, number], b: [number, number, number]) => lines.push([a, b]);
    if (shape === 2) {
        add([-ARM, 0, 0], [ARM, 0, 0]);
        add([0, -ARM, 0], [0, ARM, 0]);
    } else if (shape === 3) {
        const d = ARM / Math.SQRT2;
        add([-d, -d, 0], [d, d, 0]);
        add([-d, d, 0], [d, -d, 0]);
    } else if (shape === 4) {
        add([0, -ARM, 0], [0, ARM, 0]);
    }
    if (hasCircle) {
        const seg = 48;
        for (let i = 0; i < seg; i++) {
            const a0 = (i / seg) * Math.PI * 2, a1 = ((i + 1) / seg) * Math.PI * 2;
            add([Math.cos(a0) * HALF, Math.sin(a0) * HALF, 0], [Math.cos(a1) * HALF, Math.sin(a1) * HALF, 0]);
        }
    }
    if (hasSquare) {
        add([-HALF, -HALF, 0], [HALF, -HALF, 0]);
        add([HALF, -HALF, 0], [HALF, HALF, 0]);
        add([HALF, HALF, 0], [-HALF, HALF, 0]);
        add([-HALF, HALF, 0], [-HALF, -HALF, 0]);
    }
    return lines;
}

export function PointEntityMarker({ layerName, color = '#ffffff', rotation = 0 }: { layerName?: string; color?: string; rotation?: number }) {
    const pdmode = useSystemVarsStore((s) => s.PDMODE);
    const pdsize = useSystemVarsStore((s) => s.PDSIZE);
    const camera = useThree((s) => s.camera);
    const viewport = useThree((s) => s.size);
    const groupRef = useRef<THREE.Group>(null);

    const defPoints = isDefPointsLayer(layerName);
    const mode = defPoints ? 0 : Math.max(0, Math.round(pdmode));
    const shape = mode & 15;
    const hasCircle = (mode & 32) !== 0;
    const hasSquare = (mode & 64) !== 0;

    const lines = useMemo(() => glyphLines(shape, hasCircle, hasSquare), [shape, hasCircle, hasSquare]);

    // Zoom-stable relative sizes: 0 → 5% of the viewport height; <0 → percent.
    useFrame(() => {
        const g = groupRef.current;
        if (!g) return;
        let worldHeight: number;
        if (camera instanceof THREE.OrthographicCamera) {
            worldHeight = (camera.top - camera.bottom) / (camera.zoom || 1);
        } else {
            const dist = Math.abs(camera.position.z);
            worldHeight = 2 * dist * Math.tan(((camera as THREE.PerspectiveCamera).fov * Math.PI) / 360);
        }
        const s = pdsize > 0
            ? pdsize
            : pdsize === 0
                ? worldHeight * 0.05
                : (Math.abs(pdsize) / 100) * worldHeight;
        g.scale.setScalar(Math.max(1e-6, s));
        g.visible = shape !== 1;
    });

    if (shape === 1) {
        // PDMODE 1: nothing drawn (the pick target stays via the parent group).
        return <group ref={groupRef} visible={false} />;
    }

    return (
        <group ref={groupRef} rotation={[0, 0, rotation]}>
            {shape === 0 && (
                <mesh>
                    <circleGeometry args={[0.04, 16]} />
                    <meshBasicMaterial color={color} />
                </mesh>
            )}
            {lines.map((pts, i) => (
                <Line key={i} points={pts} color={color} lineWidth={1} />
            ))}
        </group>
    );
}
